import React, { useEffect, useState } from "react";
import { ScrollText, Filter, ShieldCheck, ShieldAlert, ShieldX, MonitorX, RefreshCcw, Smartphone } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

const ROLE_LABEL = { super_admin: "Super Admin", team_member: "Team", client: "Kunden-Admin", client_staff: "Mitarbeiter", anonymous: "Gast" };

const CE_TYPE = {
  client_error: { text: "Frontend-Fehler", cls: "text-amber-400", Icon: MonitorX },
  version_update: { text: "App aktualisiert", cls: "text-[#A3E635]", Icon: RefreshCcw },
};

const SEC_LABEL = {
  login_erfolg: { text: "Anmeldung erfolgreich", cls: "text-[#A3E635]", Icon: ShieldCheck },
  login_fehlgeschlagen: { text: "Anmeldung fehlgeschlagen", cls: "text-amber-400", Icon: ShieldAlert },
  login_gesperrt: { text: "Anmeldung gesperrt", cls: "text-red-400", Icon: ShieldX },
};

function renderChanges(changes) {
  if (!changes || Object.keys(changes).length === 0) return <span className="text-gray-600">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(changes).map(([k, v]) => {
        let txt;
        if (v && typeof v === "object" && "zu" in v) {
          const von = v.von === undefined || v.von === null || v.von === "" ? "—" : String(v.von);
          txt = `${k}: ${von} → ${String(v.zu)}`;
        } else {
          txt = `${k}: ${String(v)}`;
        }
        return <span key={k} className="text-[11px] px-2 py-0.5 rounded bg-white/5 text-gray-300 max-w-[320px] truncate">{txt}</span>;
      })}
    </div>
  );
}

export default function AuditLog() {
  const { t } = useI18n();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "audit");
  const [entries, setEntries] = useState(null);
  const [security, setSecurity] = useState(null);
  const [feEvents, setFeEvents] = useState(null);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
  }, []);
  useEffect(() => {
    const url = filter ? `/admin/audit-log?client_id=${filter}&limit=200` : `/admin/audit-log?limit=200`;
    api.get(url).then((r) => setEntries(r.data));
  }, [filter]);
  useEffect(() => {
    if (tab === "security" && security === null) {
      api.get("/admin/security-log?limit=200").then((r) => setSecurity(r.data)).catch(() => setSecurity([]));
    }
    if (tab === "frontend" && feEvents === null) {
      api.get("/admin/client-events?limit=200").then((r) => setFeEvents(r.data)).catch(() => setFeEvents([]));
    }
  }, [tab, security, feEvents]);

  return (
    <div className="fade-in">
      <PageHeader title={t("Protokoll & Audit-Log")} subtitle={t("Wer hat wann was geändert · Login-Sicherheitsereignisse")}>
        {tab === "audit" && (
          <div className="flex items-center gap-2 bg-[#121217] border border-white/10 rounded-lg px-3 py-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="audit-filter"
              className="bg-transparent text-sm focus:outline-none text-gray-200">
              <option value="">{t("Alle Kunden")}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
        )}
      </PageHeader>

      <div className="flex gap-1 mb-5 bg-[#121217] border border-white/10 rounded-lg p-1 w-fit">
        <button data-testid="tab-audit" onClick={() => setTab("audit")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${tab === "audit" ? "bg-[#A3E635] text-black" : "text-gray-400 hover:text-white"}`}>
          <ScrollText className="w-4 h-4" /> {t("Aktivität")}
        </button>
        <button data-testid="tab-security" onClick={() => setTab("security")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${tab === "security" ? "bg-[#A3E635] text-black" : "text-gray-400 hover:text-white"}`}>
          <ShieldCheck className="w-4 h-4" /> {t("Sicherheitsprotokoll")}
        </button>
        <button data-testid="tab-frontend" onClick={() => setTab("frontend")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${tab === "frontend" ? "bg-[#A3E635] text-black" : "text-gray-400 hover:text-white"}`}>
          <MonitorX className="w-4 h-4" /> {t("Frontend-Fehler")}
        </button>
      </div>

      {tab === "audit" && (
        !entries ? <Spinner /> : entries.length === 0 ? <Empty text={t("Keine Einträge")} /> : (
          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">{t("Zeitpunkt")}</th>
                    <th className="px-4 py-3">{t("Wer")}</th>
                    <th className="px-4 py-3">{t("Aktion")}</th>
                    <th className="px-4 py-3">{t("Kunde")}</th>
                    <th className="px-4 py-3">{t("Was geändert")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {entries.map((a) => (
                    <tr key={a.id} className="hover:bg-white/[0.02]" data-testid={`audit-row-${a.id}`}>
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{(a.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium">{a.actor_name || t("System")}</div>
                        <div className="text-[11px] text-gray-500">{t(ROLE_LABEL[a.actor_role] || a.actor_role || "")}</div>
                      </td>
                      <td className="px-4 py-3"><span className="font-medium text-white">{a.action}</span></td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{a.company_name || "—"}</td>
                      <td className="px-4 py-3">{renderChanges(a.changes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      {tab === "security" && (
        !security ? <Spinner /> : security.length === 0 ? <Empty text={t("Keine Login-Ereignisse")} /> : (
          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="security-table">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">{t("Zeitpunkt")}</th>
                    <th className="px-4 py-3">{t("Ereignis")}</th>
                    <th className="px-4 py-3">{t("E-Mail")}</th>
                    <th className="px-4 py-3">{t("IP-Adresse")}</th>
                    <th className="px-4 py-3">{t("Detail")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {security.map((e) => {
                    const meta = SEC_LABEL[e.event] || { text: e.event, cls: "text-gray-300", Icon: ShieldAlert };
                    const Icon = meta.Icon;
                    return (
                      <tr key={e.id} className="hover:bg-white/[0.02]" data-testid={`security-row-${e.id}`}>
                        <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                        <td className={`px-4 py-3 font-medium whitespace-nowrap ${meta.cls}`}>
                          <span className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4" />{t(meta.text)}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{e.email || "—"}</td>
                        <td className="px-4 py-3 text-gray-400 font-mono text-xs whitespace-nowrap">{e.ip || "—"}</td>
                        <td className="px-4 py-3 text-gray-400">{e.detail || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      {tab === "frontend" && (
        !feEvents ? <Spinner /> : feEvents.length === 0 ? (
          <Empty text={t("Keine Frontend-Ereignisse — alles stabil.")} />
        ) : (
          <Card className="overflow-hidden p-0">
            <div className="px-4 py-3 border-b border-white/10 text-xs text-gray-400 flex items-center gap-2">
              <MonitorX className="w-4 h-4 text-amber-400 shrink-0" />
              {t("Abgefangene Fehler wurden dem Kunden als freundliche Meldung angezeigt (kein roter Fehlerschirm).")}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="frontend-table">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">{t("Zeitpunkt")}</th>
                    <th className="px-4 py-3">{t("Typ")}</th>
                    <th className="px-4 py-3">{t("Meldung / Komponente")}</th>
                    <th className="px-4 py-3">{t("Seite")}</th>
                    <th className="px-4 py-3">{t("Wer")}</th>
                    <th className="px-4 py-3">{t("Gerät")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {feEvents.map((e) => {
                    const meta = CE_TYPE[e.type] || { text: e.type, cls: "text-gray-300", Icon: MonitorX };
                    const Icon = meta.Icon;
                    return (
                      <tr key={e.id} className="hover:bg-white/[0.02]" data-testid={`frontend-row-${e.id}`}>
                        <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                        <td className={`px-4 py-3 font-medium whitespace-nowrap ${meta.cls}`}>
                          <span className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4" />{t(meta.text)}</span>
                        </td>
                        <td className="px-4 py-3 max-w-[380px]">
                          <div className="text-gray-200 break-words">{e.message || "—"}</div>
                          {e.component && <div className="text-[11px] text-gray-500 truncate">{e.component}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-400 font-mono text-xs whitespace-nowrap">{e.path || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-medium">{e.actor_name || t("Gast")}</div>
                          <div className="text-[11px] text-gray-500">{e.company_name || t(ROLE_LABEL[e.actor_role] || e.actor_role || "")}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" />{e.device || "—"}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}
    </div>
  );
}
